import React, { useState } from "react";
import { motion } from "framer-motion";

const SidebarContent = () => {
  const [selectedOption, setSelectedOption] = useState("option1");
  const contentData = {
    option1: {
      title: "We Are Experienced",
      description:
        "With over 30 years of combined industry experience, our leadership team has worked across every facet of the cannabis sector—from cultivation and genetics development to supply chain management and retail strategy. We've collaborated with some of the most innovative brands in the business, collectively helping generate over $100 million in gross revenue. This isn't just a number—it's a reflection of our ability to execute, innovate, and scale effectively.\n\nOur deep-rooted experience gives us a strategic edge in navigating the complexities of cannabis production. We understand the nuances of growing premium-quality flower in both large-scale and small-batch environments. From selecting optimal strains and stabilizing genetics, to fine-tuning nutrient regimens and managing microclimates for terpene preservation—we handle it all with care and scientific precision.\n\nBut it doesn’t stop at cultivation. Our team is well-versed in post-harvest processing, state-specific compliance, quality assurance protocols, and packaging strategies that align with consumer trends and regulatory shifts. Every decision we make is backed by years of hard-earned knowledge, allowing us to produce consistent, clean, and potent cannabis products that consumers love and retailers trust.\n\nWhen you work with us, you're tapping into a legacy of experience that is constantly evolving. We don’t rest on our laurels—we stay current with the latest research, tech, and techniques to ensure we remain at the cutting edge of cannabis innovation.",
      image: "/images/experienced1.jpg",
    },
    option2: {
      title: "We Are Your Partners",
      description:
        "True partnership means more than just fulfilling orders—it’s about sharing a common vision and growing together. At the heart of our business is a belief in collaboration, transparency, and mutual success. We treat every client like a long-term partner, investing time, creativity, and expertise into understanding your goals and helping bring them to life.\n\nWhether you’re a startup looking to develop your first product line or a seasoned operator seeking more reliable supply and customization, we become an extension of your team. We assist with strain selection, brand alignment, and even market positioning. Want to create something the market hasn’t seen before? We’ll work with you to bring it from concept to shelf.\n\nWe pride ourselves on clear communication and flexible production processes that allow us to tailor solutions for each unique client. From small-batch boutique runs to high-volume product launches, our facility is designed to scale with your needs—without compromising quality. Our on-site team collaborates closely with clients on everything from cannabinoid profiles and terpene expression to sustainable packaging solutions that tell a compelling story.\n\nOur partnership approach also means that we're proactive, not reactive. We regularly share industry insights, compliance updates, and market trends to ensure you're always ahead of the curve. With us, you’re gaining more than just a supply chain partner—you’re gaining a growth catalyst who shares your passion and is fully invested in your success.",
      image: "/images/partners.jpg",
    },
    option3: {
      title: "We Are Nimble",
      description:
        "In the ever-evolving world of cannabis, adaptability isn’t optional—it’s essential. Our nimble framework allows us to adjust quickly to changing client needs, shifting market trends, and evolving compliance regulations. Unlike rigid corporate structures, our team thrives on dynamic problem-solving, creative thinking, and speed-to-execution.\n\nWe understand that innovation often means experimentation. Whether it’s testing new cultivars, refining grow protocols, or building out limited-edition product drops, we offer rapid prototyping and real-time adjustments that large-scale operations simply can't match. We’ve built our infrastructure to be lean and agile—ready to accommodate special requests, fluctuating volumes, and collaborative product development without the red tape.\n\nNeed to pivot quickly due to a market shift? Looking to explore white-label opportunities for a seasonal release? We’re equipped and ready to help you execute fast. Our modular production flow, in-house cultivation and post-processing team, and tight-knit operational model allow us to respond with efficiency and precision.\n\nBut agility isn’t just about speed—it’s about strategic responsiveness. Our team anticipates shifts in consumer behavior, regulatory landscapes, and technological advancements. We’re constantly optimizing workflows and investing in smarter systems to enhance output, reduce risk, and give our clients a competitive edge.\n\nWith us, you’ll find a rare combination of structure and flexibility—one that empowers innovation without sacrificing consistency. We’re not just reactive—we’re proactive, committed to evolving with you, and always ready for what’s next.",
      image: "/images/nimble.jpg",
    },
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
  };

  const titleVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  const descriptionVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, delay: 0.3 } },
  };

  const particleVariant = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
      opacity: 1,
      scale: [1, 1.5, 1],
      x: ["-100px", "100px"],
      y: ["-100px", "100px"],
      transition: {
        repeat: Infinity,
        repeatType: "loop",
        duration: 10,
        ease: "easeInOut",
      },
    },
  };

  const imageHoverVariant = {
    hover: {
      scale: 1.1,
      rotate: [0, 3, -3, 0],
      transition: {
        duration: 0.6,
        ease: "easeInOut",
      },
    },
  };

  return (
    <div className="flex flex-col md:flex-row bg-black text-gray-100 min-h-screen">
      {/* Sidebar Section */}
      <motion.div
        className="w-full md:w-72 bg-gradient-to-b from-black via-green-800 to-green-600 text-white p-8 space-y-6 md:sticky top-0 h-auto md:h-screen overflow-y-auto rounded-lg shadow-lg"
        initial={{ x: -250 }}
        animate={{ x: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <h2 className="text-4xl font-extrabold mb-6">Why Work With Us</h2>
        <ul className="space-y-6">
          {Object.keys(contentData).map((key) => (
            <li key={key}>
              <motion.button
                className={`w-full text-left text-lg font-semibold px-5 py-4 rounded-lg focus:outline-none transition-all duration-300 hover:bg-green-500 hover:text-black shadow-md ${
                  selectedOption === key ? "bg-green-500 text-black" : ""
                }`}
                onClick={() => handleOptionClick(key)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {contentData[key].title}
              </motion.button>
            </li>
          ))}
        </ul>
      </motion.div>

      {/* Main Content Section */}
      <div className="flex-1 p-10 rounded-lg relative overflow-hidden">
        {/* Particle Effects */}
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
          variants={particleVariant}
          initial="hidden"
          animate="visible"
        >
          <div className="w-12 h-12 bg-green-300 rounded-full opacity-50 shadow-xl"></div>
        </motion.div>

        <motion.div
          className="absolute top-3/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2 z-10"
          variants={particleVariant}
          initial="hidden"
          animate="visible"
        >
          <div className="w-12 h-12 bg-green-500 rounded-full opacity-50 shadow-lg"></div>
        </motion.div>

        <motion.div
          className="absolute top-1/4 right-1/4 transform -translate-x-1/2 -translate-y-1/2 z-10"
          variants={particleVariant}
          initial="hidden"
          animate="visible"
        >
          <div className="w-16 h-16 bg-green-400 rounded-full opacity-50 shadow-lg"></div>
        </motion.div>

        <motion.div
          className="absolute bottom-1/4 right-1/4 transform -translate-x-1/2 -translate-y-1/2 z-10"
          variants={particleVariant}
          initial="hidden"
          animate="visible"
        >
          <div className="w-10 h-10 bg-green-200 rounded-full opacity-50 shadow-lg"></div>
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-5xl font-extrabold mb-8 text-gray-100 border-b-4 border-gray-500 pb-4"
          variants={titleVariant}
          initial="hidden"
          animate="visible"
        >
          {contentData[selectedOption].title}
        </motion.h1>

        {/* Content and Image */}
        <div className="flex flex-col lg:flex-row space-y-10 lg:space-x-12 lg:space-y-0">
          <motion.div
            className="w-full lg:w-1/2"
            variants={imageHoverVariant}
            whileHover="hover"
          >
            <img
              src={contentData[selectedOption].image}
              alt={contentData[selectedOption].title}
              className="w-full rounded-xl shadow-2xl transform transition duration-300"
            />
          </motion.div>

          <motion.div
            className="w-full lg:w-1/2 flex items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <motion.div
              className="w-full lg:w-[80%] xl:w-[90%] flex flex-col justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              {contentData[selectedOption].description
                .split("\n")
                .map((para, index) => (
                  <motion.p
                    key={index}
                    className="text-lg leading-relaxed text-gray-300 mb-4"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.2, duration: 0.5 }}
                  >
                    {para}
                  </motion.p>
                ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SidebarContent;
